import React, { useEffect } from 'react';
import axios from 'axios';

import { useActiveUser } from 'shared/store/modules/user/hooks';
import { useActiveCustomer } from 'shared/store/modules/customers/hooks';

import { Content } from './Content';
import { Header } from './Header';
import { Navigation } from './Navigation';
import { Router } from './Router';
import { Theme } from './Theme';

import styles from './ApplicationLayout.styles';
import { useSelector } from 'react-redux';
import { useAppDispatch } from 'shared/hooks/useAppDispatch';

import { toggleNav } from 'shared/store/modules/nav/actions';
import { setMaintenanceMessage } from 'shared/store/modules/systemMaintenanceMessage';
import {
  selectActiveMaintenanceMessage,
  selectSystemMaintenanceShowUntilTime
} from 'shared/store/modules/systemMaintenanceMessage/selectors';
import { getIsNavOpen } from 'shared/store/modules/nav/selectors';
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useLocation } from 'react-router-dom';
import { useSessionTimer } from 'shared/hooks/useSessionTimer';

export const DisplaySystemMessage = ({
  systemMaintenanceMessage,
  mobile = false,
  untilTime
}: {
  systemMaintenanceMessage: string | null;
  mobile?: boolean;
  untilTime: string | null;
}) => {
  if (!systemMaintenanceMessage) return null;
  if (untilTime && new Date() > new Date(untilTime)) return null;

  return (
    <div className={`system-message ${mobile ? 'mobile' : ''}`}>
      <FontAwesomeIcon icon={faInfoCircle} className="mr-2 icon" />
      <div>{systemMaintenanceMessage}</div>
    </div>
  );
};

export function ApplicationLayout() {
  useActiveUser();
  useActiveCustomer();
  useSessionTimer();
  const dispatch = useAppDispatch();

  useEffect(() => {
    const FIVE_MINUTES = 5 * 60 * 1000;
    const fetchSystemMessage = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/system-messaging`);
        dispatch(
          setMaintenanceMessage({
            message: res.data.systemMaintenanceMessage ?? null,
            showUntilTime: res.data.systemMaintenanceShowUntilTime ?? null
          })
        );
      } catch {
        // Leave existing message in place on network error
      }
    };
    fetchSystemMessage();
    const interval = setInterval(fetchSystemMessage, FIVE_MINUTES);
    return () => clearInterval(interval);
  }, [dispatch]);

  const systemMaintenanceShowUntilTime = useSelector(selectSystemMaintenanceShowUntilTime) as string | null;

  useEffect(() => {
    if (!systemMaintenanceShowUntilTime) return;
    const delay = new Date(systemMaintenanceShowUntilTime).getTime() - Date.now();
    if (delay <= 0) {
      dispatch(setMaintenanceMessage({ message: null, showUntilTime: null }));
      return;
    }
    const timeout = setTimeout(() => {
      dispatch(setMaintenanceMessage({ message: null, showUntilTime: null }));
    }, delay);
    return () => clearTimeout(timeout);
  }, [systemMaintenanceShowUntilTime, dispatch]);

  const location = useLocation();

  const isCheckout = location.pathname.includes('checkout') || location.pathname.includes('confirmation');
  const isNavOpen = useSelector(getIsNavOpen);
  const systemMaintenanceMessage = useSelector(selectActiveMaintenanceMessage) as string | null;

  return (
    <Theme>
      <div css={styles}>
        <DisplaySystemMessage
          systemMaintenanceMessage={systemMaintenanceMessage}
          untilTime={systemMaintenanceShowUntilTime}
        />
        <div className="wrapper">
          {!isCheckout && <Header />}

          <div
            className="layout"
            onClick={() => (isNavOpen ? dispatch(toggleNav()) : {})}
          >
            {!isCheckout && <Navigation />}

            <Content>
              <Router />
            </Content>
          </div>
        </div>
      </div>
    </Theme>
  );
}
